import { CONVERSATION_ACTIVITY_EVENTS } from './messaging-internal-events';
import { MODEL_NAMES } from '../constants/index';
import { registerDestructor } from '@ember/destroyable';
import Service, { service } from '@ember/service';

/**
 * Handles conversation draft activity-bus events
 */
export default class ConversationDraftEventsService extends Service {
  @service messaging;
  @service messagingInternalEvents;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;

  #onReplace = payload => this.handleReplace(payload);
  #onDestroy = payload => this.handleDestroy(payload);

  constructor() {
    super(...arguments);
    this.messagingInternalEvents.on(CONVERSATION_ACTIVITY_EVENTS.REPLACE, this.#onReplace);
    this.messagingInternalEvents.on(CONVERSATION_ACTIVITY_EVENTS.DESTROYED, this.#onDestroy);

    registerDestructor(this, () => {
      this.messagingInternalEvents.off(CONVERSATION_ACTIVITY_EVENTS.REPLACE, this.#onReplace);
      this.messagingInternalEvents.off(CONVERSATION_ACTIVITY_EVENTS.DESTROYED, this.#onDestroy);
    });
  }

  handleReplace = async ({ replacedConversationId, newConversation }) => {
    try {
      const replacedId = String(replacedConversationId);
      const capturedDraft = this.#captureMessageDraft(replacedId);
      const newRecord = this.#pushNewConversation(newConversation);

      this.#transferMessageDraft(capturedDraft, newRecord);

      await this.messagingNavigation.replaceConversationInNavigationState(replacedId, newRecord);
      this.messaging.replaceConversationInList(replacedId, newRecord);
      this.#unloadConversation(replacedId);
    } catch (err) {
      this.messagingLogging.error(err, {
        context: 'conversation-draft-events',
        event: 'replace',
        replacedConversationId,
      });
    }
  };

  handleDestroy = async ({ conversation_id: conversationId }) => {
    try {
      const id = String(conversationId);

      this.messagingNavigation.invalidateHistory(id);

      if (this.messagingNavigation.isViewingConversation(id)) {
        await this.messagingNavigation.toConversationList({ recordHistory: false });
      }

      this.messaging.removeConversationFromList(id);
      this.#unloadConversation(id);
    } catch (err) {
      this.messagingLogging.error(err, {
        context: 'conversation-draft-events',
        event: 'destroyed',
        conversationId,
      });
    }
  };

  #pushNewConversation(payload) {
    this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, String(payload.data.id));
  }

  #captureMessageDraft(replacedId) {
    const record = this.store.peekRecord(MODEL_NAMES.CONVERSATION, replacedId);
    return record?.messageDraft;
  }

  #transferMessageDraft(capturedDraft, newRecord) {
    if (capturedDraft) {
      newRecord.messageDraft ||= capturedDraft;
    }
  }

  #unloadConversation(id) {
    const record = this.store.peekRecord(MODEL_NAMES.CONVERSATION, id);
    if (!record) {
      return;
    }

    this.store.unloadRecord(record);
  }
}
